import { t } from './i18n.js';

export interface RouteStep {
  instruction: string;
  distanceM: number;
  durationSec: number;
  travelMode: 'driving' | 'ferry';
}

type OsrmManeuver = { type: string; modifier?: string; bearing_after?: number };
export type OsrmStep = { distance: number; duration: number; name: string; mode?: string; maneuver: OsrmManeuver };

function bearingToCardinal(b: number): string {
  const dirs = ['north', 'northeast', 'east', 'southeast', 'south', 'southwest', 'west', 'northwest'];
  return t(`route_directions.${dirs[Math.round(b / 45) % 8]}`);
}

function turnInstructionKey(modifier: string | undefined): string {
  switch (modifier) {
    case 'sharp left': return 'route_directions.sharpLeft';
    case 'sharp right': return 'route_directions.sharpRight';
    case 'slight left': return 'route_directions.slightLeft';
    case 'slight right': return 'route_directions.slightRight';
    case 'left': return 'route_directions.left';
    case 'right': return 'route_directions.right';
    case 'uturn': return 'route_directions.uturn';
    default: return 'route_directions.turn';
  }
}

function roadSuffix(name: string, key: 'route_directions.onto' | 'route_directions.on'): string {
  return name ? t(key).replace('{name}', name) : '';
}

function buildInstruction(step: OsrmStep): string {
  const { type, modifier, bearing_after } = step.maneuver;
  const n = step.name;
  if (step.mode === 'ferry') return n ? t('route_directions.ferryNamed').replace('{name}', n) : t('route_directions.ferry');
  const onto = roadSuffix(n, 'route_directions.onto');
  const on = roadSuffix(n, 'route_directions.on');
  switch (type) {
    case 'depart': return (bearing_after === undefined
      ? t('route_directions.continue')
      : t('route_directions.head').replace('{direction}', bearingToCardinal(bearing_after))) + on;
    case 'arrive': return t('route_directions.arrive');
    case 'turn': return t(turnInstructionKey(modifier)) + onto;
    case 'new name':
    case 'continue': return t('route_directions.continue') + onto;
    case 'merge': return t('route_directions.merge') + onto;
    case 'fork':
      return modifier?.includes('left') ? t('route_directions.keepLeft') + onto : t('route_directions.keepRight') + onto;
    case 'on ramp': return t('route_directions.ramp') + onto;
    case 'off ramp': return t('route_directions.exit') + onto;
    case 'end of road':
      return modifier?.includes('left') ? t('route_directions.left') + onto : t('route_directions.right') + onto;
    case 'roundabout':
    case 'rotary': return t('route_directions.roundabout') + onto;
    default: return t('route_directions.continue') + on;
  }
}

const MICRO_STEP_THRESHOLD_M = 15;

function mergeSteps(raw: OsrmStep[]): OsrmStep[] {
  if (!raw.length) return raw;
  const out: OsrmStep[] = [];
  for (const step of raw) {
    const prev = out[out.length - 1];
    const isMicro = step.distance < MICRO_STEP_THRESHOLD_M && step.maneuver.type !== 'arrive' && step.maneuver.type !== 'depart';
    const sameMode = prev && step.mode === prev.mode;
    const sameDir = sameMode && step.maneuver.type === prev.maneuver.type && step.maneuver.modifier === prev.maneuver.modifier && !step.name;
    if (prev && ((isMicro && sameMode) || sameDir)) {
      out[out.length - 1] = {
        ...prev,
        name: prev.name || step.name,
        distance: prev.distance + step.distance,
        duration: prev.duration + step.duration,
      };
    } else {
      out.push(step);
    }
  }
  return out;
}

export function buildRouteDirections(legs: ReadonlyArray<{ steps?: OsrmStep[] }>): RouteStep[] {
  const allSteps: RouteStep[] = [];
  for (const leg of legs) {
    const raw = leg.steps ?? [];
    const merged = mergeSteps(raw);
    for (const step of merged) {
      if (step.maneuver.type === 'arrive') continue;
      allSteps.push({
        instruction: buildInstruction(step),
        distanceM: step.distance,
        durationSec: step.duration,
        travelMode: step.mode === 'ferry' ? 'ferry' : 'driving',
      });
    }
  }
  allSteps.push({ instruction: t('route_directions.arrive'), distanceM: 0, durationSec: 0, travelMode: 'driving' });
  return allSteps;
}
